// @ts-check
// Configurações da instituição (doc 11, seções 3.1 e 3.2): Geral (nome, ícone e identidade visual, flag WORKSPACE) e Segurança
// (link público de convite e domínios de e-mail aprovados, flag SECURITY). Contrato 08 de configurações.
import { html } from 'htm/preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { PageCard, PageHeader } from '../page/page-card.js';
import { SettingsSection } from './settings-pages.js';
import { ImageUploadField } from './image-upload-field.js';
import { BrandIdentitySection } from './brand-identity-section.js';
import { copyText } from './settings-members-page.js';
import { hasCurrentPermissionFlag, replaceCurrentTenantLocally, sessionState } from '../../data/metadata-store.js';
import {
  addApprovedDomain, fetchSecuritySettings, fetchWorkspace, generatePublicInvite, removeApprovedDomain, revokePublicInvite, updateWorkspace, uploadImage,
} from '../../data/workspace-settings.js';
import { listRoles } from '../../data/team.js';
import { Link, searchParamsState, updateSearchParams } from '../../app/router.js';
import { Button, IconButton } from '../../ui/button.js';
import { Tag } from '../../ui/chip.js';
import { EmptyState, Skeleton } from '../../ui/feedback.js';
import { Icon } from '../../ui/icon.js';
import { Modal } from '../../ui/modal.js';
import { showErrorSnackbar, showSuccessSnackbar } from '../../ui/snackbar.js';
import { t } from '../../i18n/index.js';

const NAME_SAVE_DELAY_MILLISECONDS = 500;
const MAXIMUM_DISPLAY_NAME_LENGTH = 80;

/** @param {string} key */
function useDocumentTitle(key) {
  useEffect(() => {
    document.title = `${t(key)} - ${t('navigation.settings')}`;
  }, []);
}

/** @param {{ icon: string, title: string, children: any }} props */
function SettingsPage({ icon, title, children }) {
  return html`<${PageCard} header=${html`<${PageHeader} icon=${icon} title=${title} />`}>
    <div class="settings-page">${children}</div>
  <//>`;
}

/** Sem a flag, a página explica em vez de mostrar campos que a API recusaria. @param {{ icon: string, title: string }} props */
function NoPermissionPage({ icon, title }) {
  return html`<${SettingsPage} icon=${icon} title=${title}>
    <${EmptyState} icon="IconLock" title=${t('workspaceSettings.noPermission.title')} subtitle=${t('workspaceSettings.noPermission.subtitle')} />
  <//>`;
}

export function SettingsGeneralPage() {
  useDocumentTitle('workspaceSettings.general.title');
  const tenant = sessionState.value?.tenant;
  const canEdit = hasCurrentPermissionFlag('WORKSPACE');
  const [displayName, setDisplayName] = useState(tenant?.displayName ?? '');
  const [saveStatus, setSaveStatus] = useState(/** @type {'idle' | 'saving' | 'saved'} */ ('idle'));
  const savedNameRef = useRef(tenant?.displayName ?? '');
  const isDirtyRef = useRef(false);
  const isNameMissing = displayName.trim() === '';

  // nome e logo atuais (outra pessoa pode ter mudado desde o login)
  useEffect(() => {
    if (!canEdit) return;
    fetchWorkspace()
      .then((data) => {
        if (!data?.workspace) return;
        replaceCurrentTenantLocally(data.workspace);
        // não sobrescreve o que a pessoa já começou a digitar
        if (isDirtyRef.current) return;
        savedNameRef.current = data.workspace.displayName;
        setDisplayName(data.workspace.displayName);
      })
      .catch(() => {});
  }, [canEdit]);

  useEffect(() => {
    const next = displayName.trim().replace(/\s+/g, ' ');
    if (!canEdit || next === '' || next === savedNameRef.current) return undefined;
    const timer = setTimeout(async () => {
      setSaveStatus('saving');
      try {
        const workspace = await updateWorkspace({ displayName: next });
        savedNameRef.current = workspace.displayName;
        replaceCurrentTenantLocally(workspace);
        setSaveStatus('saved');
      } catch (error) {
        setSaveStatus('idle');
        showErrorSnackbar(error);
      }
    }, NAME_SAVE_DELAY_MILLISECONDS);
    return () => clearTimeout(timer);
  }, [displayName]);

  if (!canEdit) return html`<${NoPermissionPage} icon="IconSettings" title=${t('workspaceSettings.general.title')} />`;

  /** @param {File} file */
  const uploadLogo = async (file) => {
    const uploaded = await uploadImage(file);
    replaceCurrentTenantLocally(await updateWorkspace({ logoFileUrl: uploaded.url }));
    showSuccessSnackbar(t('workspaceSettings.general.logoSaved'));
  };

  const removeLogo = async () => {
    try {
      replaceCurrentTenantLocally(await updateWorkspace({ logoFileUrl: null }));
    } catch (error) {
      showErrorSnackbar(error);
    }
  };

  const host = `${tenant?.slug ?? ''}.${window.location.host.split('.').slice(1).join('.') || window.location.host}`;
  return html`<${SettingsPage} icon="IconSettings" title=${t('workspaceSettings.general.title')}>
    <${SettingsSection} title=${t('workspaceSettings.general.logo')} description=${t('workspaceSettings.general.logoDescription')}>
      <${ImageUploadField} imageUrl=${tenant?.logoUrl ?? null} placeholder=${displayName || tenant?.displayName || ''} seed=${tenant?.id ?? displayName} shape="squared"
        cropTitle=${t('workspaceSettings.general.cropTitle')} onUpload=${uploadLogo} onRemove=${removeLogo} />
    <//>
    <${BrandIdentitySection} />
    <${SettingsSection} title=${t('workspaceSettings.general.name')} description=${t('workspaceSettings.general.nameDescription')}>
      <label class="form-field">
        <span class="form-field-label">${t('workspaceSettings.general.nameLabel')}</span>
        <input class="text-input" name="displayName" value=${displayName} maxLength=${MAXIMUM_DISPLAY_NAME_LENGTH} data-error=${isNameMissing ? 'true' : undefined}
          placeholder=${t('workspaceSettings.general.namePlaceholder')}
          onInput=${(event) => {
            isDirtyRef.current = true;
            setDisplayName(/** @type {HTMLInputElement} */ (event.currentTarget).value);
          }} />
      </label>
      <span class="settings-field-status" role="status" data-danger=${isNameMissing ? 'true' : undefined}>
        ${isNameMissing ? t('workspaceSettings.general.nameRequired')
          : saveStatus === 'saving' ? t('workspaceSettings.saving') : saveStatus === 'saved' ? t('workspaceSettings.saved') : ''}
      </span>
    <//>
    <${SettingsSection} title=${t('workspaceSettings.general.address')} description=${t('workspaceSettings.general.addressDescription')}>
      <div class="settings-info-card">
        <${Icon} name="IconWorld" size=${18} />
        <span class="settings-info-card-text">${host}</span>
      </div>
    <//>
  <//>`;
}

/**
 * @param {{ publicInvite: import('../../data/workspace-settings.js').PublicInvite | null, onChanged: (publicInvite: import('../../data/workspace-settings.js').PublicInvite | null) => void }} props
 */
function PublicInviteSection({ publicInvite, onChanged }) {
  const [isBusy, setIsBusy] = useState(false);
  const [isConfirmingRevoke, setIsConfirmingRevoke] = useState(false);

  const generate = async () => {
    setIsBusy(true);
    try {
      const created = await generatePublicInvite();
      onChanged(created);
      if (await copyText(created.url)) showSuccessSnackbar(t('workspaceSettings.security.publicInvite.generatedAndCopied'));
    } catch (error) {
      showErrorSnackbar(error);
    } finally {
      setIsBusy(false);
    }
  };

  const revoke = async () => {
    setIsBusy(true);
    try {
      await revokePublicInvite();
      onChanged(null);
      setIsConfirmingRevoke(false);
      showSuccessSnackbar(t('workspaceSettings.security.publicInvite.revoked'));
    } catch (error) {
      showErrorSnackbar(error);
    } finally {
      setIsBusy(false);
    }
  };

  return html`<${SettingsSection} title=${t('workspaceSettings.security.publicInvite.title')} description=${t('workspaceSettings.security.publicInvite.description')}>
    ${publicInvite
      ? html`<div class="settings-copy-row">
          <input class="text-input" value=${publicInvite.url} readonly aria-label=${t('workspaceSettings.security.publicInvite.title')}
            onFocus=${(event) => /** @type {HTMLInputElement} */ (event.currentTarget).select()} />
          <${Button} title=${t('workspaceSettings.security.publicInvite.copy')} icon="IconCopy" variant="secondary" size="small"
            onClick=${async () => {
              if (await copyText(publicInvite.url)) showSuccessSnackbar(t('workspaceSettings.security.publicInvite.copied'));
            }} />
        </div>
        <div class="settings-inline-actions">
          <${Button} title=${t('workspaceSettings.security.publicInvite.regenerate')} icon="IconRefresh" variant="secondary" size="small" disabled=${isBusy} onClick=${generate} />
          <${Button} title=${t('workspaceSettings.security.publicInvite.revoke')} icon="IconX" variant="secondary" accent="danger" size="small" disabled=${isBusy}
            onClick=${() => setIsConfirmingRevoke(true)} />
        </div>`
      : html`<div class="settings-inline-actions">
          <${Button} title=${t('workspaceSettings.security.publicInvite.generate')} icon="IconLink" accent="blue" size="small" isLoading=${isBusy} onClick=${generate} />
        </div>`}
    ${isConfirmingRevoke
      ? html`<${Modal} title=${t('workspaceSettings.security.publicInvite.revokeTitle')} subtitle=${t('workspaceSettings.security.publicInvite.revokeSubtitle')}
          confirmLabel=${t('workspaceSettings.security.publicInvite.revoke')} confirmAccent="danger" isLoading=${isBusy}
          onClose=${() => setIsConfirmingRevoke(false)} onConfirm=${revoke} />`
      : null}
  <//>`;
}

/**
 * @param {{ approvedDomains: import('../../data/workspace-settings.js').ApprovedDomain[], onChanged: () => void }} props
 */
function ApprovedDomainsSection({ approvedDomains, onChanged }) {
  const [domain, setDomain] = useState('');
  const [email, setEmail] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [busyId, setBusyId] = useState(/** @type {string | null} */ (null));
  const [devValidationUrl, setDevValidationUrl] = useState(/** @type {string | null} */ (null));

  const submit = async (/** @type {Event} */ event) => {
    event.preventDefault();
    if (!domain.trim()) return;
    setIsSaving(true);
    try {
      const result = await addApprovedDomain({ domain: domain.trim(), ...(email.trim() ? { email: email.trim() } : {}) });
      setDomain('');
      setEmail('');
      setDevValidationUrl(result.devValidationUrl ?? null);
      showSuccessSnackbar(result.approvedDomain.isValidated
        ? t('workspaceSettings.security.domains.addedValidated', { domain: result.approvedDomain.domain })
        : t('workspaceSettings.security.domains.addedPending', { email: result.approvedDomain.validationEmail }));
      onChanged();
    } catch (error) {
      showErrorSnackbar(error);
    } finally {
      setIsSaving(false);
    }
  };

  /** @param {import('../../data/workspace-settings.js').ApprovedDomain} approvedDomain */
  const remove = async (approvedDomain) => {
    setBusyId(approvedDomain.id);
    try {
      await removeApprovedDomain(approvedDomain.id);
      showSuccessSnackbar(t('workspaceSettings.security.domains.removed', { domain: approvedDomain.domain }));
      onChanged();
    } catch (error) {
      showErrorSnackbar(error);
    } finally {
      setBusyId(null);
    }
  };

  return html`<${SettingsSection} title=${t('workspaceSettings.security.domains.title')} description=${t('workspaceSettings.security.domains.description')}>
    ${approvedDomains.length === 0
      ? html`<p class="team-empty">${t('workspaceSettings.security.domains.empty')}</p>`
      : html`<div class="settings-table">
          ${approvedDomains.map((approvedDomain) => html`<div key=${approvedDomain.id} class="settings-table-row settings-domain-row">
            <span class="settings-table-name"><${Icon} name="IconAt" size=${16} />${approvedDomain.domain}</span>
            <span>
              ${approvedDomain.isValidated
                ? html`<${Tag} color="green" text=${t('workspaceSettings.security.domains.validated')} />`
                : html`<${Tag} color="orange" text=${t('workspaceSettings.security.domains.pending', { email: approvedDomain.validationEmail })} />`}
            </span>
            <${IconButton} icon="IconTrash" size="small" variant="tertiary" accent="danger" disabled=${busyId === approvedDomain.id}
              ariaLabel=${t('workspaceSettings.security.domains.removeFor', { domain: approvedDomain.domain })} onClick=${() => remove(approvedDomain)} />
          </div>`)}
        </div>`}
    <form class="settings-domain-form" onSubmit=${submit}>
      <label class="form-field">
        <span class="form-field-label">${t('workspaceSettings.security.domains.domainLabel')}</span>
        <input class="text-input" name="domain" value=${domain} placeholder=${t('workspaceSettings.security.domains.domainPlaceholder')} autocomplete="off"
          onInput=${(event) => setDomain(/** @type {HTMLInputElement} */ (event.currentTarget).value)} />
      </label>
      <label class="form-field">
        <span class="form-field-label">${t('workspaceSettings.security.domains.emailLabel')}</span>
        <input class="text-input" name="validationEmail" type="email" value=${email} placeholder=${t('workspaceSettings.security.domains.emailPlaceholder')} autocomplete="off"
          onInput=${(event) => setEmail(/** @type {HTMLInputElement} */ (event.currentTarget).value)} />
      </label>
      <div class="settings-domain-submit">
        <${Button} title=${t('workspaceSettings.security.domains.add')} icon="IconPlus" accent="blue" size="small" type="submit" isLoading=${isSaving} disabled=${!domain.trim()} />
      </div>
    </form>
    <span class="settings-field-status">${t('workspaceSettings.security.domains.emailHint')}</span>
    ${devValidationUrl
      ? html`<div class="settings-dev-link" role="note">
          <span>${t('workspaceSettings.security.domains.devValidation')}</span>
          <a href=${devValidationUrl}>${t('workspaceSettings.security.domains.devValidationLink')}</a>
        </div>`
      : null}
  <//>`;
}

export function SettingsSecurityPage() {
  useDocumentTitle('workspaceSettings.security.title');
  const canEdit = hasCurrentPermissionFlag('SECURITY');
  const [settings, setSettings] = useState(/** @type {Awaited<ReturnType<typeof fetchSecuritySettings>> | null} */ (null));
  const [defaultRoleLabel, setDefaultRoleLabel] = useState(/** @type {string | null} */ (null));
  const validation = searchParamsState.value.get('domainValidation');

  const reload = () => fetchSecuritySettings().then(setSettings).catch((error) => {
    showErrorSnackbar(error);
    setSettings({ publicInvite: null, approvedDomains: [] });
  });

  useEffect(() => {
    if (!canEdit) return;
    reload();
    listRoles()
      .then((data) => setDefaultRoleLabel(data.roles.find((role) => role.id === data.defaultRoleId)?.label ?? null))
      .catch(() => {});
  }, [canEdit]);

  // volta do link de validação do domínio (e-mail)
  useEffect(() => {
    if (!validation) return;
    if (validation === 'validated' || validation === 'already_validated') showSuccessSnackbar(t('workspaceSettings.security.domains.validationDone'));
    else showErrorSnackbar(new Error(t('workspaceSettings.security.domains.validationFailed')));
    updateSearchParams({ domainValidation: null });
  }, [validation]);

  if (!canEdit) return html`<${NoPermissionPage} icon="IconKey" title=${t('workspaceSettings.security.title')} />`;
  return html`<${SettingsPage} icon="IconKey" title=${t('workspaceSettings.security.title')}>
    <${SettingsSection} title=${t('workspaceSettings.security.defaultRole.title')} description=${t('workspaceSettings.security.defaultRole.description')}>
      <div class="settings-info-card">
        <${Icon} name="IconShieldLock" size=${18} />
        <span class="settings-info-card-text">${defaultRoleLabel ?? '...'}</span>
        ${hasCurrentPermissionFlag('ROLES') ? html`<${Link} href="/settings/roles" className="settings-info-card-link">${t('workspaceSettings.security.defaultRole.change')}<//>` : null}
      </div>
    <//>
    ${settings
      ? html`<${PublicInviteSection} publicInvite=${settings.publicInvite} onChanged=${(publicInvite) => setSettings({ ...settings, publicInvite })} />
          <${ApprovedDomainsSection} approvedDomains=${settings.approvedDomains} onChanged=${reload} />`
      : html`<${Skeleton} height=${120} radius=${8} />`}
  <//>`;
}
